import { useState } from 'react';
import { Send, LogIn, Loader2, CheckCircle } from 'lucide-react';
import { useCreateBidMutation, useAppSelector, selectIsAuthenticated, selectCurrentUser } from '@nimbazaar/store';
import { useToast } from './Toast';

interface BidSubmissionFormProps {
  adId: string;
  adSlug: string;
  isOwner: boolean;
  existingBidUserId?: string;
  onLoginClick: () => void;
}

export function BidSubmissionForm({ adId, adSlug, isOwner, existingBidUserId, onLoginClick }: BidSubmissionFormProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector(selectCurrentUser);
  const toast = useToast();

  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createBid, { isLoading }] = useCreateBidMutation();

  // Don't show form to ad owner
  if (isOwner) {
    return null;
  }

  // Check if user already bid
  const hasExistingBid = currentUser && existingBidUserId === currentUser.id;

  if (hasExistingBid) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center gap-3 text-green-600">
          <CheckCircle className="w-6 h-6" />
          <div>
            <h3 className="font-semibold">Bid Submitted</h3>
            <p className="text-sm text-gray-500">You have already placed a bid on this ad.</p>
          </div>
        </div>
      </div>
    );
  }

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const priceNum = parseFloat(price);

    if (!price || isNaN(priceNum) || priceNum < 0.01) {
      newErrors.price = 'Please enter a valid price (min Rs. 0.01)';
    }
    if (!description || description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    if (description.length > 1000) {
      newErrors.description = 'Description must be 1000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      await createBid({
        adId,
        price: parseFloat(price),
        description,
      }).unwrap();

      toast.success('Bid Submitted!', 'Your bid has been placed successfully.');
      setPrice('');
      setDescription('');
      setErrors({});
    } catch (error) {
      const apiError = error as { data?: { error?: { message?: string; code?: string } } };
      const code = apiError?.data?.error?.code;
      const message = apiError?.data?.error?.message || 'Failed to submit bid.';

      if (code === 'ALREADY_BID') {
        toast.info('Already Bid', message);
      } else if (code === 'FORBIDDEN') {
        toast.error('Cannot Bid', message);
      } else {
        toast.error('Error', message);
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Send className="w-5 h-5 text-secondary-500" />
        Submit a Bid
      </h2>

      {!isAuthenticated ? (
        <div className="text-center py-6">
          <p className="text-gray-500 mb-4">Sign in to place a bid on this ad.</p>
          <button
            onClick={onLoginClick}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
          >
            <LogIn className="w-5 h-5" />
            Login to Bid
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Price Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Price
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">Rs.</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Enter your price"
                disabled={isLoading}
                className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed ${
                  errors.price ? 'border-red-500' : 'border-gray-200'
                }`}
              />
            </div>
            {errors.price && (
              <p className="mt-1 text-sm text-red-500">{errors.price}</p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what you're offering (min 10 characters)"
              rows={3}
              disabled={isLoading}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none disabled:bg-gray-50 disabled:cursor-not-allowed ${
                errors.description ? 'border-red-500' : 'border-gray-200'
              }`}
            />
            <div className="flex justify-between mt-1">
              {errors.description ? (
                <p className="text-sm text-red-500">{errors.description}</p>
              ) : (
                <span />
              )}
              <span className="text-xs text-gray-400">{description.length}/1000</span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-secondary-500 hover:bg-secondary-600 disabled:bg-secondary-300 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Bid
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
